import { Link, useParams } from 'react-router-dom';
import { Building2, MapPin, Users, Globe, Shield, Sparkles, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import { Helmet } from 'react-helmet-async';

function CompanyProfileSkeleton() {
  return (
    <div className="min-h-screen bg-[#080a19] pb-20">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 space-y-6">
        <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
        <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="h-12 w-12 animate-pulse rounded-xl bg-white/10" />
                <div className="space-y-2">
                  <div className="h-4 w-48 animate-pulse rounded bg-white/10" />
                  <div className="h-3 w-32 animate-pulse rounded bg-white/10" />
                </div>
              </div>
              <div className="h-3 w-full animate-pulse rounded bg-white/10" />
              <div className="flex gap-2">
                <div className="h-6 w-24 animate-pulse rounded-full bg-white/10" />
                <div className="h-6 w-32 animate-pulse rounded-full bg-white/10" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6 p-8 pt-0">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={`company-skeleton-section-${index}`} className="space-y-2">
                  <div className="h-4 w-40 animate-pulse rounded bg-white/10" />
                  <div className="space-y-1">
                    <div className="h-3 w-full animate-pulse rounded bg-white/10" />
                    <div className="h-3 w-5/6 animate-pulse rounded bg-white/10" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.03]">
            <CardContent className="space-y-4 p-6">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={`aside-skeleton-${index}`} className="h-4 w-full animate-pulse rounded bg-white/10" />
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export function CompanyProfile() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const { data, isPending, isError, error, refetch } = useCompanyProfile(companySlug);

  if (isPending) {
    return <CompanyProfileSkeleton />;
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-red-500/40 bg-red-500/10">
          <CardContent className="space-y-4 p-8 text-center text-sm text-red-100">
            <p className="text-2xl font-semibold text-white">We couldn&apos;t load this company profile.</p>
            <p>{error.message}</p>
            <Button onClick={() => refetch()} className="bg-gold-gradient text-black hover:brightness-110">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const company = data?.company;
  if (!company) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <Card className="border-white/10 bg-white/[0.02]">
          <CardContent className="space-y-4 p-8 text-center">
            <p className="text-2xl font-semibold text-white">Company not found</p>
            <p className="text-sm text-gray-300">
              This profile may have been removed or the link is incorrect. Discover other employers building inclusive teams.
            </p>
            <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
              <Link to="/jobs">Browse jobs</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const openRoles = data?.openRoles ?? [];
  const highlights = data?.highlights?.length
    ? data.highlights
    : [
        'Dedicated coaching and phased onboarding for career comebacks.',
        'Flexible working practices with hybrid and remote options.',
        'Measurable inclusion targets reviewed quarterly with Lighthouse.',
      ];
  const testimonials = data?.testimonials ?? [];
  const nearbyEmployers = data?.nearbyEmployers ?? [];
  const r2Base = import.meta.env.VITE_R2_PUBLIC_BASE_URL ?? 'https://cdn.lighthouse.jobs';
  const gallery =
    data?.gallery ??
    (company as { gallery?: string[] })?.gallery ??
    [`${r2Base}/office-1.jpg`, `${r2Base}/office-2.jpg`, `${r2Base}/mentorship.jpg`];
  const cultureDeckUrl =
    data?.cultureDeckUrl ?? (company as { cultureDeckUrl?: string })?.cultureDeckUrl ?? undefined;

  const website =
    (company as { websiteUrl?: string })?.websiteUrl ?? (company as { website?: string })?.website ?? null;
  const companyName = company.name ?? 'Employer';
  const companyIndustry = company.industry ?? 'Inclusive employer';
  const companyLocation = company.location ?? 'Remote-first';
  const companyEmployees = (company as { employees?: string })?.employees ?? 'Team size not shared';
  const companyStatus =
    company.status === 'verified'
      ? 'Verified by Lighthouse'
      : company.status
        ? company.status
        : 'Lighthouse partner';
  const aboutText =
    (company as { about?: string })?.about ??
    company.description ??
    'This employer partners with Lighthouse to build inclusive careers for returners.';
  const companyId = (company as { id?: number | string })?.id;

  const fullWebsite =
    website && website.startsWith('http') ? website : website ? `https://${website}` : undefined;
  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: companyName,
    description: aboutText,
    url: fullWebsite,
    address: {
      '@type': 'PostalAddress',
      addressLocality: companyLocation,
    },
  };

  return (
    <main aria-labelledby="company-profile-heading" className="min-h-screen bg-[#080a19] pb-20">
      <Helmet>
        <title>{`${companyName} | Lighthouse Employers`}</title>
        <meta
          name="description"
          content={`${companyName} · ${companyIndustry}. Discover open roles and culture highlights for returners.`}
        />
        {fullWebsite && <link rel="canonical" href={fullWebsite} />}
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd)}</script>
      </Helmet>
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <Link to="/jobs" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-[#f7ef8a]">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to jobs
        </Link>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <article aria-labelledby="company-profile-heading">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gold-gradient text-[#101225] shadow-lg shadow-[#d2ac47]/35">
                  <Building2 className="h-6 w-6" aria-hidden />
                </span>
                <div>
                  <CardTitle id="company-profile-heading" className="text-2xl text-white">{companyName}</CardTitle>
                  <p className="text-sm text-gray-300">{companyIndustry}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-gray-300">
                <span className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-[#ba9036]" aria-hidden />
                  {companyLocation}
                </span>
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#ba9036]" aria-hidden />
                  {companyEmployees} team size
                </span>
                <span className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-[#ba9036]" aria-hidden />
                  {companyStatus}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className="border-[#d2ac47]/40 bg-[#ba9036]/15 text-[#f7ef8a]">
                  {(company as { subscription?: string })?.subscription ?? 'Premium'} partner
                </Badge>
                {website && (
                  <Badge variant="outline" className="border-white/10 text-gray-200">
                    <a
                      href={fullWebsite}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1"
                    >
                      <Globe className="h-3.5 w-3.5" aria-hidden />
                      {website.replace(/^https?:\/\//, '')}
                    </a>
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-8 p-8 pt-0">
              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-white">About</h2>
                <p className="text-sm text-gray-300 leading-6">{aboutText}</p>
              </section>

              {gallery.length > 0 && (
                <section className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-white">Inside {companyName}</h3>
                    {cultureDeckUrl && (
                      <Button
                        variant="outline"
                        className="border-[#d2ac47]/40 text-[#f7ef8a] hover:bg-[#ba9036]/10"
                        asChild
                      >
                        <a href={cultureDeckUrl} target="_blank" rel="noreferrer">
                          Download culture deck
                        </a>
                      </Button>
                    )}
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    {gallery.map((src) => (
                      <figure key={src} className="overflow-hidden rounded-2xl border border-white/10">
                        <img
                          src={src}
                          alt={`${companyName} culture`}
                          className="h-36 w-full object-cover transition-transform duration-500 hover:scale-105"
                          loading="lazy"
                        />
                      </figure>
                    ))}
                  </div>
                </section>
              )}

              <section className="space-y-3">
                <h3 className="text-base font-semibold text-white">Why returners thrive here</h3>
                <ul className="space-y-2 text-sm text-gray-300">
                  {highlights.map((highlight) => (
                    <li key={highlight} className="flex items-start gap-2">
                      <Sparkles className="mt-0.5 h-4 w-4 text-[#ba9036]" aria-hidden />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-white">Open roles</h3>
                  <Button asChild variant="ghost" className="text-[#f7ef8a] hover:text-[#f7ef8a]">
                    <Link to="/jobs">View all roles</Link>
                  </Button>
                </div>
                <div className="grid gap-4">
                  {openRoles.length === 0 && (
                    <Card className="border-white/10 bg-white/[0.02]">
                      <CardContent className="p-6 text-sm text-gray-300">
                        No live roles right now. Follow the company to be notified when new opportunities open.
                      </CardContent>
                    </Card>
                  )}
                  {openRoles.map((role) => (
                    <Card key={role.id ?? role.title} className="border-white/10 bg-white/[0.04]">
                      <CardContent className="p-5">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <Link
                              to={`/jobs/${(role as { slug?: string }).slug ?? role.id}`}
                              className="text-lg font-semibold text-white hover:text-[#f7ef8a]">
                              {role.title}
                            </Link>
                            <p className="text-sm text-gray-400">{role.location}</p>
                          </div>
                          <Badge variant="outline" className="border-white/15 text-gray-200">
                            {role.type}
                          </Badge>
                        </div>
                        <p className="mt-3 line-clamp-3 text-sm text-gray-300">{role.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              {testimonials.length > 0 && (
                <section className="space-y-4">
                  <h3 className="text-base font-semibold text-white">Returner testimonials</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {testimonials.map((testimonial) => (
                      <Card key={testimonial.id} className="border-white/10 bg-white/[0.04]">
                        <CardContent className="space-y-3 p-5 text-sm text-gray-300">
                          <p className="italic text-gray-200">&ldquo;{testimonial.quote}&rdquo;</p>
                          <p className="text-xs uppercase tracking-[0.3em] text-gray-500">
                            {testimonial.person} · {testimonial.title}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </section>
              )}
            </CardContent>
          </Card>
          </article>

          <aside className="space-y-6" aria-label="Company actions and nearby employers">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="space-y-4 p-6">
                <h2 className="text-lg font-semibold text-white">Connect with the team</h2>
                <p className="text-sm text-gray-300">
                  Join Lighthouse to signal interest, follow updates, and be fast-tracked for matching roles as they launch.
                </p>
                <Button className="w-full bg-gold-gradient text-black hover:brightness-110">
                  Follow company
                </Button>
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10" asChild>
                  <Link to="/candidate">Create candidate profile</Link>
                </Button>
              </CardContent>
            </Card>

            {nearbyEmployers.length > 0 && (
              <Card className="border-white/10 bg-white/[0.03]">
                <CardHeader>
                  <CardTitle className="text-base text-white">Other employers in {companyLocation}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {nearbyEmployers.map((employer) => (
                    <div key={employer.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
                      <Link to={`/company/${(employer as { slug?: string }).slug ?? employer.id}`} className="text-sm font-semibold text-white hover:text-[#f7ef8a]">
                        {employer.name}
                      </Link>
                      <p className="text-xs text-gray-400">{employer.industry}</p>
                      <p className="text-xs text-gray-500">{employer.employees} employees</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </aside>
        </div>
      </div>
    </main>
  );
}
